/**
 * Puts the prescription into the active-workout screen: a target line on each
 * exercise block, and prescribed values filled into set rows that are still
 * blank.
 *
 * Seam: the set table is built inside one ~8KB minified template literal
 * (`renderExerciseBlock`, closure-local to `renderNewWorkout`), which the plan
 * forbids editing. `render()` reassigns `innerHTML` on every state change, so
 * the nodes are new each time — which is exactly what `onRendered` keys on. No
 * `renderNewWorkout` wrapper is needed here: replacing the markup replaces the
 * elements, so the observer re-fires on its own.
 *
 * **Prefilling is safe to do, for one specific reason.** `sbSaveWorkout`
 * (core.js:2480) persists only sets where `completed` is true. A prefilled set
 * the user never ticks is never written, so a prescription cannot become
 * history it did not earn. We still refuse to prefill anything the user has
 * touched: only a genuinely blank set (weight 0, reps 0) is filled, so the
 * carry-forward in `addSetToExercise` always wins over the engine.
 */

import { onRendered, legacy } from '../../runtime/legacy.js';
import { prescribeFor } from './prescribe.js';
import { summarise, explain, isColdStart } from './format.js';

/** Pulls the two indices out of `toggleSetComplete(0,2)` in the row's button. */
function readIndices(tr) {
  const btn = tr.querySelector('.set-complete-btn');
  const match = btn?.getAttribute('onclick')?.match(/toggleSetComplete\((\d+)\s*,\s*(\d+)\)/);
  if (!match) return null;
  return { exIdx: Number(match[1]), setIdx: Number(match[2]) };
}

function banner(result) {
  const el = document.createElement('div');
  el.className = 'set-prescription';
  if (isColdStart(result)) {
    el.innerHTML = `<span class="set-prescription-why">${explain(result)}</span>`;
    return el;
  }
  el.innerHTML = `
    <span class="set-prescription-target">Target: ${summarise(result)}</span>
    <span class="set-prescription-why">${explain(result)}</span>`;
  return el;
}

function prefillRows(block, result) {
  if (isColdStart(result)) return;
  const AppState = legacy('AppState');
  const updateWeight = legacy('updateSetWeight');
  const updateSet = legacy('updateSet');
  const toDisplay = legacy('kgToDisplay');
  if (typeof updateWeight !== 'function' || typeof updateSet !== 'function') return;

  block.querySelectorAll('tbody tr').forEach((tr) => {
    const idx = readIndices(tr);
    if (!idx) return;

    const set = AppState?.activeWorkout?.exercises?.[idx.exIdx]?.sets?.[idx.setIdx];
    if (!set || set.completed) return;
    // Only a genuinely blank set. Anything the user (or the carry-forward in
    // addSetToExercise) already put here is theirs, not ours to overwrite.
    if ((set.weight_kg || 0) !== 0 || (set.reps || 0) !== 0) return;

    const target = result.prescription[idx.setIdx] || result.prescription[result.prescription.length - 1];
    if (!target) return;

    const [weightInput, repsInput] = tr.querySelectorAll('input[type="number"]');
    if (weightInput && target.weightKg > 0) {
      const shown = typeof toDisplay === 'function' ? toDisplay(target.weightKg) : target.weightKg;
      weightInput.value = shown;
      // `updateSetWeight` stores `displayToKg(shown)`, and that round trip is
      // lossy in imperial: 80kg → 176.4lbs → 80.01kg. Call it so `_displayWeight`
      // and the storage write happen exactly as they normally would, then put the
      // exact prescribed kg back. Without this the engine reads its own
      // prescription back a hundredth heavier than it issued it.
      updateWeight(idx.exIdx, idx.setIdx, shown);
      set.weight_kg = target.weightKg;
    }
    if (repsInput && target.reps > 0) {
      repsInput.value = target.reps;
      updateSet(idx.exIdx, idx.setIdx, 'reps', target.reps);
    }
  });
}

export function installSetPrefill() {
  onRendered('.exercise-block', (block) => {
    // Runs in demo mode too: demo workouts are never persisted, so a prefilled
    // value there can reach nothing but the screen it is drawn on.
    const firstRow = block.querySelector('tbody tr');
    const idx = firstRow && readIndices(firstRow);
    if (!idx) return;

    const AppState = legacy('AppState');
    const exercise = AppState?.activeWorkout?.exercises?.[idx.exIdx];
    if (!exercise?.exercise_id) return;

    const result = prescribeFor(exercise.exercise_id, { muscleGroup: exercise.muscle_group });

    const header = block.querySelector('.exercise-block-header');
    if (header && !block.querySelector('.set-prescription')) {
      header.insertAdjacentElement('afterend', banner(result));
    }

    prefillRows(block, result);
  });
}
